import React, { useState } from "react";
import { Copy, Sparkles, Send, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

var API = process.env.REACT_APP_BACKEND_URL + "/api";

var messageTemplates = [
  {
    id: "first_contact",
    category: "Primer Contacto",
    title: "Bienvenida inicial",
    template: "Hola [NOMBRE],\n\nGracias por contactar con Elite Motor Group. He recibido tu consulta sobre el [VEHICULO].\n\nMe encantaría ayudarte a encontrar el coche perfecto para ti. ¿Tienes disponibilidad para una llamada breve esta semana?\n\nQuedo a tu disposición.\n\nUn saludo,"
  },
  {
    id: "options",
    category: "Envío de Opciones",
    title: "Presentación de vehículos",
    template: "Hola [NOMBRE],\n\nTe envío las opciones que he seleccionado para ti:\n\n[DETALLES_VEHICULOS]\n\nTodos los vehículos pasan por nuestra inspección de 150 puntos y cuentan con garantía Elite Motor Group.\n\n¿Te gustaría que comentemos alguna de estas opciones con más detalle?\n\nQuedo a la espera de tus comentarios.\n\nUn saludo,"
  },
  {
    id: "followup",
    category: "Seguimiento",
    title: "Seguimiento comercial",
    template: "Hola [NOMBRE],\n\n¿Qué tal? Te escribo para saber si has tenido oportunidad de revisar las opciones que te envié.\n\nSi tienes alguna duda o necesitas más información, estaré encantado de ayudarte.\n\n¿Hablamos?\n\nUn saludo,"
  },
  {
    id: "reservation",
    category: "Reserva",
    title: "Confirmación de reserva",
    template: "Hola [NOMBRE],\n\n¡Enhorabuena por tu decisión!\n\nTe confirmo la reserva del [VEHICULO]. A partir de este momento, el vehículo queda apartado para ti.\n\nPróximos pasos:\n- Señal de reserva: [IMPORTE]\n- Plazo estimado de entrega: [PLAZO]\n\nTe mantendré informado en todo momento del estado de tu vehículo.\n\nGracias por confiar en Elite Motor Group.\n\nUn saludo,"
  },
  {
    id: "delivery",
    category: "Entrega",
    title: "Cita de entrega",
    template: "Hola [NOMBRE],\n\n¡Tu [VEHICULO] está listo para la entrega!\n\nTe propongo la siguiente fecha para la recogida:\n📅 [FECHA]\n📍 Instalaciones Elite Motor Group\n\nDurante la entrega te explicaré todos los detalles del vehículo y resolveremos cualquier duda.\n\n¿Te viene bien esta fecha?\n\nUn saludo,"
  },
  {
    id: "thanks",
    category: "Postventa",
    title: "Agradecimiento post-entrega",
    template: "Hola [NOMBRE],\n\nEspero que estés disfrutando de tu nuevo [VEHICULO].\n\nHa sido un placer atenderte y ayudarte a encontrar el coche perfecto para ti.\n\nRecuerda que estoy a tu disposición para cualquier cosa que necesites. Y si conoces a alguien que esté buscando coche, ya sabes dónde encontrarme.\n\n¡Gracias por confiar en Elite Motor Group!\n\nUn saludo,"
  }
];

function MessagesPage({ clients }) {
  var _useState = useState("");
  var selectedTemplate = _useState[0];
  var setSelectedTemplate = _useState[1];

  var _useState2 = useState("");
  var message = _useState2[0];
  var setMessage = _useState2[1];

  var _useState3 = useState("");
  var selectedClient = _useState3[0];
  var setSelectedClient = _useState3[1];

  var _useState4 = useState(false);
  var isImproving = _useState4[0];
  var setIsImproving = _useState4[1];

  function handleTemplateSelect(templateId) {
    setSelectedTemplate(templateId);
    var template = messageTemplates.find(function(t) { return t.id === templateId; });
    if (template) {
      var text = template.template;
      if (selectedClient) {
        var client = clients.find(function(c) { return c.id === parseInt(selectedClient); });
        if (client) {
          text = text.replace("[NOMBRE]", client.name.split(" ")[0]);
          text = text.replace("[VEHICULO]", client.interest || "[VEHICULO]");
        }
      }
      setMessage(text);
    }
  }

  function handleClientSelect(clientId) {
    setSelectedClient(clientId);
    if (message) {
      var client = clients.find(function(c) { return c.id === parseInt(clientId); });
      if (client) {
        var text = message;
        text = text.replace(/\[NOMBRE\]/g, client.name.split(" ")[0]);
        text = text.replace(/\[VEHICULO\]/g, client.interest || "[VEHICULO]");
        setMessage(text);
      }
    }
  }

  function handleImprove() {
    if (!message.trim()) {
      toast.error("Escribe un mensaje primero");
      return;
    }
    setIsImproving(true);
    axios.post(API + "/improve-text", { text: message, style: "professional" })
      .then(function(res) {
        setMessage(res.data.improved);
        toast.success("Mensaje mejorado con IA");
      })
      .catch(function() {
        toast.error("Error al mejorar el mensaje");
      })
      .finally(function() {
        setIsImproving(false);
      });
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(message);
    toast.success("Copiado al portapapeles");
  }

  return (
    <div className="h-screen overflow-y-auto p-8 bg-slate-50" data-testid="messages-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Asistente de Comunicación</h1>
        <p className="text-slate-500">Biblioteca de mensajes profesionales</p>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Templates List */}
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="font-bold text-slate-800 mb-4">Plantillas</h3>
          <div className="space-y-2">
            {messageTemplates.map(function(t) {
              return (
                <button
                  key={t.id}
                  onClick={function() { handleTemplateSelect(t.id); }}
                  className={"w-full text-left p-3 rounded-lg border transition-colors " + 
                    (selectedTemplate === t.id ? "bg-blue-50 border-blue-300" : "hover:bg-slate-50")}
                >
                  <p className="text-xs text-slate-400 mb-1">{t.category}</p>
                  <p className="font-medium text-slate-700">{t.title}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Message Editor */}
        <div className="col-span-2 bg-white rounded-xl border shadow-sm p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <Select value={selectedClient} onValueChange={handleClientSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar cliente..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(function(c) {
                    return <SelectItem key={c.id} value={String(c.id)}>{c.name} - {c.interest}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleImprove} disabled={isImproving} variant="outline" className="gap-2">
              <Sparkles size={16} className={isImproving ? "animate-spin" : ""} />
              {isImproving ? "Mejorando..." : "Mejorar con IA"}
            </Button>
          </div>

          <Textarea
            value={message}
            onChange={function(e) { setMessage(e.target.value); }}
            placeholder="Selecciona una plantilla o escribe tu mensaje..."
            className="min-h-[400px] text-sm"
          />

          <div className="flex justify-end gap-3 mt-4">
            <Button onClick={copyToClipboard} variant="outline" className="gap-2">
              <Copy size={16} /> Copiar
            </Button>
            <Button className="btn-primary gap-2">
              <Send size={16} /> Abrir WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MessagesPage;
